import { FC, useState } from 'react';
import { Plus } from 'lucide-react';
import { AdminLayout } from './AdminLayout';
import { FormModal } from '../../components/admin/FormModal';
import { ConfirmModal } from '../../components/admin/ConfirmModal';
import {
  ServiceFilters,
  ServiceTable,
  ServiceForm,
  ServiceViewModal,
} from '../../components/admin/service';
import {
  useServiceList,
  useCreateService,
  useUpdateService,
  useDeleteService,
  useToggleServiceStatus,
} from '../../hooks/admin/useAdminServices';
import type {
  AdminService,
  ServiceListQuery,
  CreateServiceRequest,
  UpdateServiceRequest,
} from '../../types/admin.types';

type ModalState = 'closed' | 'create' | 'edit' | 'view' | 'delete';

export const ServicesPage: FC = () => {
  // Query params state
  const [query, setQuery] = useState<ServiceListQuery>({ page: 1, limit: 10 });

  // Modal state
  const [modalState, setModalState] = useState<ModalState>('closed');
  const [selectedService, setSelectedService] = useState<AdminService | null>(null);

  // React Query hooks
  const { data, isLoading, error } = useServiceList(query);
  const createService = useCreateService();
  const updateService = useUpdateService();
  const deleteService = useDeleteService();
  const toggleStatus = useToggleServiceStatus();

  // Extract data from React Query response
  const services = data?.data ?? [];
  const pagination = data?.pagination ?? null;

  // Filter handlers
  const handleSearch = (search: string) =>
    setQuery((q) => ({ ...q, search: search || undefined, page: 1 }));
  const handlePageChange = (page: number) =>
    setQuery((q) => ({ ...q, page }));
  const handleCategoryChange = (category: string) =>
    setQuery((q) => ({ ...q, category: category || undefined, page: 1 }));
  const handleStatusChange = (isActive: string) =>
    setQuery((q) => ({ ...q, isActive: isActive || undefined, page: 1 }));

  // Modal handlers
  const openCreate = () => setModalState('create');
  const openEdit = (service: AdminService) => {
    setSelectedService(service);
    setModalState('edit');
  };
  const openView = (service: AdminService) => {
    setSelectedService(service);
    setModalState('view');
  };
  const openDelete = (service: AdminService) => {
    setSelectedService(service);
    setModalState('delete');
  };
  const closeModal = () => {
    setModalState('closed');
    setSelectedService(null);
  };

  // Mutation handlers
  const handleCreate = async (formData: CreateServiceRequest) => {
    await createService.mutateAsync(formData);
    closeModal();
  };

  const handleUpdate = async (formData: CreateServiceRequest) => {
    if (!selectedService) return;
    const updateData: UpdateServiceRequest = { ...formData };
    await updateService.mutateAsync({ id: selectedService.id, data: updateData });
    closeModal();
  };

  const handleDelete = async () => {
    if (!selectedService) return;
    await deleteService.mutateAsync(selectedService.id);
    closeModal();
  };

  const handleToggleStatus = async (service: AdminService) => {
    await toggleStatus.mutateAsync({ id: service.id, isActive: !service.isActive });
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-100">
              Services
            </h2>
            <p className="mt-1 text-gray-500 dark:text-slate-400">
              Manage your service offerings
            </p>
          </div>
          <button
            onClick={openCreate}
            className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            Add Service
          </button>
        </div>

        {/* Error display */}
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
            <p className="text-red-800 dark:text-red-200">
              {error instanceof Error ? error.message : 'Failed to load services'}
            </p>
          </div>
        )}

        {/* Filters */}
        <ServiceFilters
          query={query}
          onSearch={handleSearch}
          onCategoryChange={handleCategoryChange}
          onStatusChange={handleStatusChange}
        />

        {/* Table */}
        <ServiceTable
          services={services}
          pagination={pagination}
          isLoading={isLoading}
          onPageChange={handlePageChange}
          onView={openView}
          onEdit={openEdit}
          onDelete={openDelete}
          onToggleStatus={handleToggleStatus}
        />

        {/* Create Modal */}
        {modalState === 'create' && (
          <FormModal
            isOpen
            onClose={closeModal}
            title="Add New Service"
            submitText=""
            cancelText=""
            size="lg"
          >
            <ServiceForm
              isSubmitting={createService.isPending}
              onSubmit={handleCreate}
              onCancel={closeModal}
            />
          </FormModal>
        )}

        {/* Edit Modal */}
        {modalState === 'edit' && selectedService && (
          <FormModal
            isOpen
            onClose={closeModal}
            title="Edit Service"
            submitText=""
            cancelText=""
            size="lg"
          >
            <ServiceForm
              initialData={selectedService}
              isSubmitting={updateService.isPending}
              onSubmit={handleUpdate}
              onCancel={closeModal}
            />
          </FormModal>
        )}

        {/* View Modal */}
        {modalState === 'view' && selectedService && (
          <ServiceViewModal
            service={selectedService}
            isOpen
            onClose={closeModal}
          />
        )}

        {/* Delete Confirmation */}
        {modalState === 'delete' && selectedService && (
          <ConfirmModal
            isOpen
            onClose={closeModal}
            onConfirm={handleDelete}
            title="Deactivate Service"
            message={`Are you sure you want to deactivate "${selectedService.name}"? It will no longer be available for booking.`}
            confirmText="Deactivate"
            variant="danger"
            isLoading={deleteService.isPending}
          />
        )}
      </div>
    </AdminLayout>
  );
};

export default ServicesPage;
